/**
 * "Add a translation" language picker panel (#2226, image 21).
 *
 * Rendered inline inside `<ChangeLanguageModal>` when the user hits the
 * "Add a translation" button. Shows a searchable list of all languages
 * the DeepL enum knows about, minus the ones the doc already has.
 * Pick one → call the parent's `onCreate(code)` which fires
 * `useTranslateDocument`. Cancel → back to the translations list.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, AlertCircle, Coins, Loader2, Search } from 'lucide-react';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import Input from '@components/atoms/ui-kit/input.tsx';
import Button from '@components/atoms/ui-kit/button.tsx';
import { LanguageOption, allLanguages } from './language-utils.ts';
import type { TranslationQuote } from '@utils/api/documents/use-translation-quote.ts';

interface Props {
  /** Codes already present as source, translation, or already queued. */
  existingCodes: string[];
  /**
   * API-sourced quote (#2241). Shown next to Start so the user sees
   * the real price before committing. `undefined` while the quote is
   * loading. Start requires a current, eligible quote.
   */
  quote?: TranslationQuote;
  quoteLoading?: boolean;
  quoteError?: boolean;
  onRetryQuote?: () => void;
  onCancel: () => void;
  onCreate: (code: string) => Promise<void> | void;
  creating?: boolean;
}

export function AddTranslationPanel({
  existingCodes,
  quote,
  quoteLoading = false,
  quoteError = false,
  onRetryQuote,
  onCancel,
  onCreate,
  creating = false
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const canStart = !!quote?.eligible && !quoteLoading && !quoteError;
  const costParts = [
    Number(quote?.credit_amount) > 0
      ? t('translation-cost-credit', {
          amount: quote?.credit_amount,
          currency: quote?.currency
        })
      : null,
    Number(quote?.invoice_amount) > 0
      ? t('translation-cost-invoice', {
          amount: quote?.invoice_amount,
          currency: quote?.currency
        })
      : null
  ].filter(Boolean);

  const options = useMemo<LanguageOption[]>(() => {
    const taken = new Set(existingCodes.map(c => c.toUpperCase()));
    const q = query.trim().toLowerCase();
    return allLanguages().filter(o => {
      if (taken.has(o.code)) return false;
      if (!q) return true;
      return (
        o.name.toLowerCase().includes(q) || o.code.toLowerCase().includes(q)
      );
    });
  }, [existingCodes, query]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={creating}
          aria-label={t('back')}
          className="inline-flex items-center justify-center h-8 w-8 rounded-2xl hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40"
          style={{ color: getColour('icon') }}
        >
          <ArrowLeft size={16} aria-hidden />
        </button>
        <span
          className="text-sm font-semibold"
          style={{ color: getColour('normalText') }}
        >
          {t('add-a-translation')}
        </span>
      </div>

      <div className="relative">
        <Search
          size={14}
          aria-hidden
          className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ color: getColour('inactiveText') }}
        />
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder={t('search-languages')}
          aria-label={t('search-languages')}
          size="small"
          className="pl-8"
        />
      </div>

      <div
        role="listbox"
        aria-label={t('add-a-translation')}
        className="max-h-64 overflow-y-auto rounded-2xl border"
        style={{ borderColor: getColour('border') }}
      >
        {options.length === 0 ? (
          <p
            className="text-xs text-center py-6 opacity-70"
            style={{ color: getColour('inactiveText') }}
          >
            {t('no-languages-available')}
          </p>
        ) : (
          options.map(opt => {
            const isSelected = opt.code === selected;
            return (
              <button
                key={opt.code}
                type="button"
                role="option"
                aria-selected={isSelected}
                onClick={() => setSelected(opt.code)}
                disabled={creating}
                className="flex w-full items-center gap-3 px-3 py-2 text-sm text-left border-b last:border-b-0 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-40"
                style={{
                  borderColor: getColour('hairline'),
                  backgroundColor: isSelected
                    ? getColour('thirdLayer')
                    : 'transparent',
                  color: getColour('normalText')
                }}
              >
                <span className="font-semibold uppercase text-xs tabular-nums w-8 shrink-0">
                  {opt.code}
                </span>
                <span className="truncate">{opt.name}</span>
              </button>
            );
          })
        )}
      </div>

      <div className="flex items-center justify-between gap-2 pt-1">
        {quoteError ? (
          <div
            role="alert"
            className="text-xs"
            style={{ color: getColour('danger') }}
          >
            {t('translation-quote-error')}
            {onRetryQuote && (
              <button
                type="button"
                onClick={onRetryQuote}
                className="ml-2 underline"
              >
                {t('retry')}
              </button>
            )}
          </div>
        ) : quoteLoading || !quote ? (
          <span
            role="status"
            className="inline-flex items-center gap-1.5 text-xs"
          >
            <Loader2 size={12} className="animate-spin" aria-hidden />
            {t('translation-quote-loading')}
          </span>
        ) : quote.eligible ? (
          <span
            data-testid="translation-cost"
            className="inline-flex items-center gap-1.5 text-xs"
            style={{ color: getColour('inactiveText') }}
          >
            <Coins size={12} aria-hidden />
            {costParts.length > 0
              ? costParts.join(' + ')
              : quote.has_charge && quote.cost_amount && quote.currency
                ? t('translation-cost-amount', {
                    amount: quote.cost_amount,
                    currency: quote.currency,
                    defaultValue: '{{amount}} {{currency}}'
                  })
                : quote.cost_amount !== null
                  ? t('translation-cost-no-additional')
                  : t('translation-cost-free', { defaultValue: 'Free' })}
          </span>
        ) : (
          <span
            data-testid="translation-ineligible"
            className="inline-flex items-center gap-1.5 text-xs"
            style={{ color: getColour('danger') }}
          >
            <AlertCircle size={12} aria-hidden />
            {t('translation-insufficient-credit', {
              defaultValue: 'Insufficient translation credit'
            })}
          </span>
        )}
        <div className="flex items-center gap-2">
          <Button
            variant="light"
            size="sm"
            onClick={onCancel}
            disabled={creating}
            label={t('cancel')}
          />
          <Button
            variant="primary"
            size="sm"
            disabled={!selected || creating || !canStart}
            onClick={async () => {
              if (!selected || !canStart) return;
              await onCreate(selected);
            }}
            icon={
              creating ? (
                <Loader2 size={14} className="animate-spin" aria-hidden />
              ) : undefined
            }
            label={creating ? t('adding-translation') : t('start')}
          />
        </div>
      </div>
    </div>
  );
}
